import { isLandingCampaign, type LandingCampaign } from '@/shared/consts/app-links';

/**
 * TRAD-3711 — «첫 진입 출처» 기억(§D-2, docs-ops/marketing/2026-10-12-store-metadata-paste.md).
 * 첫 진입 URL 의 `utm_campaign` 이 허용 목록이면 같은 탭 세션 동안 sessionStorage 에 기억한다 → 스토어 버튼이 그 출처 이름을 붙인다.
 *
 * - 첫 진입만: 이미 기억한 값이 있으면 덮지 않는다(세션 중 다른 utm 링크로 들어와도 «첫 출처»를 유지).
 * - 허용 목록 밖·없음 → 아무것도 저장하지 않는다(버튼 자리 이름으로 떨어진다).
 * - 저장소 실패(사파리 사생활 모드·차단 등) → 기억하지 않고 기본값. 원인은 console.warn 으로 남긴다(삼키지 않는다).
 *   로그 문구는 영어 — 한글 하드코딩 래칫(TRAD-2570) 대상이라(개발자용 로그도 문자열 리터럴로 센다).
 * - 읽을 때도 허용 목록을 다시 대조한다 — 저장소 값이 오염돼도 스토어 링크로 새지 않는다.
 *
 * 저장소는 주입한다(테스트·SSR 프리렌더에서 window 없음).
 */
export const LANDING_CAMPAIGN_STORAGE_KEY = 'xt.landing_campaign';

type KeyValueStorage = Pick<Storage, 'getItem' | 'setItem'>;

const sessionStore = (): KeyValueStorage | null => (typeof window === 'undefined' ? null : window.sessionStorage);

export const readLandingCampaign = (storage: KeyValueStorage | null = sessionStore()): LandingCampaign | null => {
  if (!storage) return null;
  try {
    const value = storage.getItem(LANDING_CAMPAIGN_STORAGE_KEY);
    return isLandingCampaign(value) ? value : null;
  } catch (error) {
    console.warn('[store-attribution] failed to read landing campaign — falling back to placement name', error);
    return null;
  }
};

/** 첫 진입 URL 의 search(예: `?utm_campaign=ig_bio`)를 받아 허용값이면 기억한다. 저장했으면 true. */
export const captureLandingCampaign = (search: string, storage: KeyValueStorage | null = sessionStore()): boolean => {
  if (!storage) return false;
  try {
    if (storage.getItem(LANDING_CAMPAIGN_STORAGE_KEY) !== null) return false;
    const campaign = new URLSearchParams(search).get('utm_campaign');
    if (!isLandingCampaign(campaign)) return false;
    storage.setItem(LANDING_CAMPAIGN_STORAGE_KEY, campaign);
    return true;
  } catch (error) {
    console.warn('[store-attribution] failed to store landing campaign — not remembered', error);
    return false;
  }
};
